// src/pages/legality/components/LegalityContent.tsx
import { useSelector } from "react-redux";

import styles from "./LegalityContent.module.scss";
import { LegalityScreens } from "../../common/types/enums";
import { useTheme } from "../../common/hooks/useTheme";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";

interface LegalityContentProps {
  screen: LegalityScreens;
}

const userAgreementText = `
ПОЛЬЗОВАТЕЛЬСКОЕ СОГЛАШЕНИЕ

Настоящее Соглашение определяет условия использования веб-приложения и мобильного приложения {legalName} (ИНН {legalInn}), юридический адрес: {legalAddress}.

1. Общие положения
1.1. Использование Приложения означает полное и безоговорочное согласие Пользователя с настоящим Соглашением.
1.2. Приложение предоставляет возможность заказа товаров и услуг через сеть Интернет.

2. Регистрация и аккаунт
2.1. Для оформления заказа необходима авторизация с использованием адреса электронной почты.
2.2. Код подтверждения направляется на указанный Пользователем адрес электронной почты и является одноразовым.
2.3. Пользователь гарантирует, что указанный им адрес электронной почты принадлежит ему.

3. Оформление заказа
3.1. При оформлении заказа Пользователь предоставляет адрес доставки и контактный телефон.
3.2. После подтверждения заказа Пользователь получает уведомление на электронную почту.
3.3. Отмена заказа возможна до момента передачи его в сборку.

4. Доставка
4.1. Доставка осуществляется по адресу доставки, указанному Пользователем.
4.2. Стоимость и сроки доставки рассчитываются автоматически при оформлении заказа.

5. Заключительные положения
5.1. Оператор оставляет за собой право вносить изменения в Соглашение. Новая версия вступает в силу с момента опубликования в Приложении.
5.2. Все споры решаются путем переговоров, при недостижении согласия — в судебном порядке по месту нахождения Оператора.
`;

const privacyPolicyText = `
ПОЛИТИКА КОНФИДЕНЦИАЛЬНОСТИ

Настоящая Политика конфиденциальности действует в отношении информации, которую {legalName} (ИНН {legalInn}) получает о Пользователе при использовании им веб-приложения и мобильного приложения.

1. Какие данные мы собираем
1.1. Адрес электронной почты (используется для авторизации)
1.2. Номер телефона (указывается Пользователем при оформлении заказа)
1.3. Адрес доставки (указывается Пользователем при оформлении заказа)
1.4. История заказов

2. Как мы используем данные
2.1. Для авторизации Пользователя и отправки кодов подтверждения
2.2. Для оформления и доставки заказов
2.3. Для отправки уведомлений о статусе заказа
2.4. Для улучшения качества обслуживания

3. Передача данных третьим лицам
3.1. Курьерским службам (только телефон и адрес доставки)
3.2. Обработчику персональных данных — НОЧЕВКИНУ СЕРГЕЮ МИХАЙЛОВИЧУ (реестр РКН № 100340655), предоставляющему программную платформу по договору поручения
3.3. По требованию уполномоченных государственных органов в случаях, предусмотренных законом

4. Защита данных
4.1. Все данные хранятся на защищенных серверах на территории РФ (г. Санкт-Петербург)
4.2. Адрес электронной почты и номер телефона хранятся в зашифрованном виде (AES-256-GCM), ключи шифрования изолированы от базы данных
4.3. Доступ к данным имеют только уполномоченные сотрудники

5. Срок хранения
5.1. Данные хранятся в течение всего времени использования Пользователем Приложения
5.2. При удалении аккаунта данные удаляются в течение 30 дней
`;

const personalDataAgreementText = `
СОГЛАСИЕ
на обработку персональных данных

Настоящим свободно, своей волей и в своих интересах даю согласие {legalName} (ИНН {legalInn}), юридический адрес: {legalAddress}, на обработку моих персональных данных:

- адрес электронной почты
- номер телефона
- адрес доставки
- история заказов

на следующих условиях:

1. Цели обработки
   - создание и ведение аккаунта
   - авторизация с использованием кодов подтверждения
   - оформление и доставка заказов
   - информирование о статусе заказов
   - улучшение качества обслуживания

2. Способы обработки
   - сбор, запись, систематизация
   - накопление, хранение, уточнение
   - использование
   - передача курьерским службам (телефон, адрес доставки)
   - передача обработчику ПД НОЧЕВКИНУ С.М. (реестр РКН № 100340655) для обеспечения работы Приложения
   - удаление

3. Срок действия
   Настоящее согласие действует с момента его подтверждения в Приложении и до достижения целей обработки или отзыва согласия.

4. Отзыв согласия
   Согласие может быть отозвано путем направления письменного заявления на юридический адрес Оператора или на адрес электронной почты Оператора, указанный в Приложении.
`;

export const LegalityContent = ({ screen }: LegalityContentProps) => {
  const { theme } = useTheme();
  const cluster = useSelector(selectCurrentCluster);

  const getText = () => {
    const legalName = cluster?.legalName || "Оператор";
    const legalInn = cluster?.legalInn || "—";
    const legalAddress = cluster?.legalAddress || "—";

    const replacePlaceholders = (text: string) => {
      return text
        .replace(/{legalName}/g, legalName)
        .replace(/{legalInn}/g, legalInn)
        .replace(/{legalAddress}/g, legalAddress);
    };

    switch (screen) {
      case LegalityScreens.USER_AGREEMENT:
        return replacePlaceholders(userAgreementText);
      case LegalityScreens.PRIVACY_POLICY:
        return replacePlaceholders(privacyPolicyText);
      case LegalityScreens.PERSONAL_DATA_AGREEMENT:
        return replacePlaceholders(personalDataAgreementText);
      default:
        return replacePlaceholders(userAgreementText);
    }
  };

  const fullText = getText();

  const renderText = () => {
    const lines = fullText.trim().split("\n");

    return lines.map((line, index) => {
      const trimmed = line.trim();

      if (!trimmed) {
        return <div key={index} className={styles.spacer} />;
      }

      // Заголовки
      if (
        trimmed.startsWith("ПОЛЬЗОВАТЕЛЬСКОЕ") ||
        trimmed.startsWith("ПОЛИТИКА") ||
        trimmed.startsWith("СОГЛАСИЕ")
      ) {
        return (
          <h2
            key={index}
            className={styles.title}
            style={{ color: theme.text }}
          >
            {trimmed}
          </h2>
        );
      }

      // Нумерованные заголовки (1., 2., etc)
      if (trimmed.match(/^\d+\./)) {
        return (
          <h3
            key={index}
            className={styles.sectionTitle}
            style={{ color: theme.text }}
          >
            {trimmed}
          </h3>
        );
      }

      // Обычный текст
      return (
        <p key={index} className={styles.text} style={{ color: theme.text }}>
          {trimmed}
        </p>
      );
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.scrollView}>{renderText()}</div>
    </div>
  );
};
